import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import CookieConsentBanner from "@/shared/components/privacy/CookieConsentBanner";
import CookiePreferenceCenter from "@/shared/components/privacy/CookiePreferenceCenter";
import {
  COOKIE_CONSENT_EVENT,
  createCookieConsent,
  getCookieConsent,
  getCookieConsentDraftFromStored,
  getDefaultCookieConsentDraft,
  hasConsent as hasStoredConsent,
  saveCookieConsent,
  shouldShowCookieBanner,
  type CookieConsent,
  type CookieConsentCategory,
  type CookieConsentDraft,
} from "@/shared/cookies/cookieConsent";

type CookieConsentContextValue = {
  consent: CookieConsent | null;
  isBannerVisible: boolean;
  isPreferenceCenterOpen: boolean;
  hasConsent: (category: CookieConsentCategory) => boolean;
  openPreferenceCenter: () => void;
  closePreferenceCenter: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
};

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

function createDraftState() {
  return getDefaultCookieConsentDraft();
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [isPreferenceCenterOpen, setIsPreferenceCenterOpen] = useState(false);
  const [draft, setDraft] = useState<CookieConsentDraft>(createDraftState);

  useEffect(() => {
    const syncState = (nextConsent: CookieConsent | null) => {
      setConsent(nextConsent);
      setIsBannerVisible(nextConsent === null && shouldShowCookieBanner());
      setDraft(
        nextConsent
          ? {
              functional: nextConsent.functional,
              performance: nextConsent.performance,
              targeting: nextConsent.targeting,
            }
          : getCookieConsentDraftFromStored(),
      );
    };

    syncState(getCookieConsent());

    const handleStorage = () => {
      syncState(getCookieConsent());
    };

    const handleConsentEvent = (event: Event) => {
      const detail = (event as CustomEvent<CookieConsent | null>).detail ?? getCookieConsent();
      syncState(detail);
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener(COOKIE_CONSENT_EVENT, handleConsentEvent);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(COOKIE_CONSENT_EVENT, handleConsentEvent);
    };
  }, []);

  const persistDraft = (nextDraft: CookieConsentDraft) => {
    const nextConsent = createCookieConsent(nextDraft);
    saveCookieConsent(nextConsent);
    setConsent(nextConsent);
    setIsBannerVisible(false);
    setIsPreferenceCenterOpen(false);
  };

  const acceptAll = () => {
    persistDraft({
      functional: true,
      performance: true,
      targeting: true,
    });
  };

  const rejectAll = () => {
    persistDraft({
      functional: false,
      performance: false,
      targeting: false,
    });
  };

  const openPreferenceCenter = () => {
    setDraft(
      consent
        ? {
            functional: consent.functional,
            performance: consent.performance,
            targeting: consent.targeting,
          }
        : getCookieConsentDraftFromStored(),
    );
    setIsPreferenceCenterOpen(true);
  };

  const closePreferenceCenter = () => {
    setIsPreferenceCenterOpen(false);
  };

  const contextValue = useMemo<CookieConsentContextValue>(
    () => ({
      consent,
      isBannerVisible,
      isPreferenceCenterOpen,
      hasConsent: (category) => hasStoredConsent(category),
      openPreferenceCenter,
      closePreferenceCenter,
      acceptAll,
      rejectAll,
    }),
    [consent, isBannerVisible, isPreferenceCenterOpen],
  );

  return (
    <CookieConsentContext.Provider value={contextValue}>
      {children}
      <CookieConsentBanner
        isVisible={isBannerVisible}
        onAcceptAll={acceptAll}
        onRejectAll={rejectAll}
        onOpenSettings={openPreferenceCenter}
      />
      <CookiePreferenceCenter
        isOpen={isPreferenceCenterOpen}
        draft={draft}
        onChange={setDraft}
        onClose={closePreferenceCenter}
        onSave={() => persistDraft(draft)}
        onAcceptAll={acceptAll}
        onRejectAll={rejectAll}
      />
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);

  if (!context) {
    throw new Error("useCookieConsent must be used within CookieConsentProvider");
  }

  return context;
}
